import { STORE_NAME } from "@/utils/constants.js";

/** @typedef {{ id: string, title: string, body: string, target?: string | (() => string | null) }} WalkthroughStep */

const MOBILE_NAV_MQ = "(max-width: 768px)";
const MOBILE_FILTER_MQ = "(max-width: 1100px)";

export function mainNavTarget(tourId) {
    const desktop = `[data-tour="${tourId}"]`;
    return () => {
        if (typeof window !== "undefined" && window.matchMedia(MOBILE_NAV_MQ).matches) {
            return `.header-mobile-nav.is-open [data-tour="${tourId}"]`;
        }
        return desktop;
    };
}

/** Desktop: main nav link. Mobile: Compte menu (cart / purchases are not in the hamburger drawer). */
export function mainNavOrAccountTarget(tourId) {
    return () => {
        if (typeof window !== "undefined" && window.matchMedia(MOBILE_NAV_MQ).matches) {
            return '[data-tour="account-menu"]';
        }
        return `[data-tour="${tourId}"]`;
    };
}

function catalogFiltersTarget() {
    if (typeof window === "undefined") {
        return '[data-tour="catalog-filters"]';
    }
    return window.matchMedia(MOBILE_FILTER_MQ).matches
        ? '[data-tour="catalog-filters-fab"]'
        : '[data-tour="catalog-filters-header"]';
}

function adminDesktopMenuTarget(sectionId) {
    return () => {
        if (typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches) {
            return '[data-tour="account-menu"]';
        }
        return `[data-tour="profile-menu-${sectionId}"]`;
    };
}

/** @type {WalkthroughStep[]} */
export const GUEST_WALKTHROUGH_STEPS = [
    {
        id: "signup-banner",
        title: "Créer un compte ou se connecter",
        body: "Le bandeau vert sous le menu permet de s'inscrire ou de se connecter à tout moment. Vous pouvez aussi utiliser le menu Compte.",
        target: '[data-tour="guest-auth-banner"]'
    },
    {
        id: "welcome",
        title: `Bienvenue sur ${STORE_NAME}`,
        body: "Ce message présente les avantages de la plateforme. Parcourez le catalogue, trouvez vos pièces et commandez en ligne.",
        target: '[data-tour="home-welcome-hero"]'
    },
    {
        id: "catalogue",
        title: "Le catalogue",
        body: "Le catalogue affiche les pièces disponibles. Ouvrez une fiche produit pour voir les détails, les photos et le stock.",
        target: mainNavTarget("nav-catalogue")
    },
    {
        id: "filters",
        title: "Rechercher une pièce",
        body: "Utilisez les filtres (texte, catégorie, prix) pour affiner la liste. Sur mobile, ouvrez la recherche avec le bouton « Chercher ».",
        target: catalogFiltersTarget
    },
    {
        id: "cart",
        title: "Le panier",
        body: "Le lien Panier est visible dans le menu (ou dans Compte sur mobile). Connectez-vous pour ajouter des articles et passer commande.",
        target: mainNavOrAccountTarget("nav-cart")
    },
    {
        id: "purchases",
        title: "Mes achats",
        body: "Mes achats regroupe l'historique de vos commandes (menu principal ou Compte sur mobile). Un compte est nécessaire pour y accéder.",
        target: mainNavOrAccountTarget("nav-purchases")
    },
    {
        id: "account",
        title: "Le menu Compte",
        body: "Le menu Compte donne aussi accès au panier, aux achats, aux adresses et au profil. Connexion ou inscription en bas du menu.",
        target: '[data-tour="account-menu"]'
    },
    {
        id: "contact",
        title: "Nous contacter",
        body: "Besoin d'aide ? La page Contact regroupe téléphones, WhatsApp, e-mail et adresses des magasins.",
        target: mainNavTarget("nav-contact")
    }
];

/** @type {WalkthroughStep[]} */
export const CUSTOMER_WALKTHROUGH_STEPS = [
    {
        id: "welcome",
        title: "Votre espace client",
        body: "Vous êtes connecté. Voici les raccourcis pour commander et suivre vos achats sur le catalogue."
    },
    {
        id: "cart",
        title: "Le panier",
        body: "Ajoutez des pièces depuis le catalogue, puis ouvrez Panier (menu principal, ou Compte sur mobile) pour vérifier les quantités avant de commander.",
        target: mainNavOrAccountTarget("nav-cart")
    },
    {
        id: "purchases",
        title: "Mes achats",
        body: "Retrouvez l'historique de vos commandes, leur statut et le détail de chaque achat (menu principal, ou Compte sur mobile).",
        target: mainNavOrAccountTarget("nav-purchases")
    },
    {
        id: "account",
        title: "Le menu Compte",
        body: "Le menu Compte ouvre aussi Mon panier, vos adresses, votre profil et le changement de mot de passe.",
        target: '[data-tour="account-menu"]'
    },
    {
        id: "filters",
        title: "Rechercher une pièce",
        body: "Les filtres du catalogue restent disponibles pour affiner votre recherche à tout moment.",
        target: catalogFiltersTarget
    }
];

/** @type {WalkthroughStep[]} */
export const ADMIN_WALKTHROUGH_STEPS = [
    {
        id: "welcome",
        title: "Espace administrateur",
        body: "Vous êtes connecté en tant qu'administrateur. Voici les outils pour gérer le catalogue, les commandes et les paramètres."
    },
    {
        id: "banner",
        title: "Session admin",
        body: "Ce bandeau rappelle que vous êtes en session administrateur. Les clients ne le voient pas.",
        target: '[data-tour="admin-banner"]'
    },
    {
        id: "account",
        title: "Menu administrateur",
        body: "Le menu Compte ouvre toutes les sections de gestion : commandes, produits, lots, clients, magasins, avis et informations légales.",
        target: '[data-tour="account-menu"]'
    },
    {
        id: "orders",
        title: "Gérer les commandes",
        body: "Suivez et mettez à jour les commandes clients (statut, lignes, reçu PDF).",
        target: adminDesktopMenuTarget("orders")
    },
    {
        id: "catalog",
        title: "Catalogue et stock",
        body: "Gérez les produits et les lots d'inventaire (création, publication, prix, quantités).",
        target: adminDesktopMenuTarget("products")
    },
    {
        id: "reviews",
        title: "Modération des avis",
        body: "Consultez et modérez les avis clients laissés sur les fiches produit.",
        target: adminDesktopMenuTarget("reviews")
    },
    {
        id: "store",
        title: "Magasins et infos légales",
        body: "Mettez à jour les magasins (horaires, contacts) et les informations légales affichées sur le site.",
        target: adminDesktopMenuTarget("shops")
    }
];

export const GUEST_WALKTHROUGH_STORAGE_KEY = "guestWalkthroughCompleted";
